import moment, { Moment } from "moment";
import { Sell, SellPaymentMethod } from "../sell.entity";
import { SellProduct } from "../sell-product";

export const paymentMethodLabel: Record<SellPaymentMethod, string> = {
  [SellPaymentMethod.UNPAID]: "Em aberto",
  [SellPaymentMethod.MONEY]: "Dinheiro",
  [SellPaymentMethod.CREDIT_CARD_DEBIT]: "Cartão",
  [SellPaymentMethod.PIX]: "PIX",
  [SellPaymentMethod.CREDIT]: "Fiado",
};

export function formatBRL(value: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

function productDisplayName(line: SellProduct) {
  return line.entity.getName();
}

export type DailyPoint = {
  dayKey: string;
  label: string;
  revenue: number;
  count: number;
};

export type PaymentSlice = {
  method: SellPaymentMethod;
  label: string;
  count: number;
  revenue: number;
};

export type TopProductRow = { name: string; quantity: number; revenue: number };

export type SalesSummary = {
  /** Número de dias no intervalo (eixo do gráfico diário). */
  dayCount: number;
  /** Intervalo legível para UI e IA. */
  periodLabelHuman: string;
  totalRevenue: number;
  saleCount: number;
  avgTicket: number;
  byPayment: PaymentSlice[];
  daily: DailyPoint[];
  topProducts: TopProductRow[];
  sellsInPeriod: Sell[];
};

export function buildSalesSummaryForRange(
  sells: Sell[],
  rangeStart: Date,
  rangeEnd: Date
): SalesSummary {
  const start = moment(rangeStart).startOf("day");
  const end = moment(rangeEnd).endOf("day");
  const periodLabelHuman = `${start.format("DD MMM YYYY")} — ${end.format("DD MMM YYYY")}`;

  const sellsInPeriod = sells.filter((s) => {
    const c = s.getCompletedAt();
    if (!c || s.getPaymentMethod() === SellPaymentMethod.UNPAID) return false;
    return c.isSameOrAfter(start) && c.isSameOrBefore(end);
  });

  let totalRevenue = 0;
  const paymentAcc = new Map<
    SellPaymentMethod,
    { count: number; revenue: number }
  >();
  const dayAcc = new Map<string, { revenue: number; count: number }>();
  const productAcc = new Map<string, { quantity: number; revenue: number }>();

  for (const sell of sellsInPeriod) {
    const total = sell.getSellTotal();
    totalRevenue += total;

    const pm = sell.getPaymentMethod();
    const p = paymentAcc.get(pm) ?? { count: 0, revenue: 0 };
    p.count += 1;
    p.revenue += total;
    paymentAcc.set(pm, p);

    const completed = sell.getCompletedAt() as Moment;
    const dayKey = completed.format("YYYY-MM-DD");
    const d = dayAcc.get(dayKey) ?? { revenue: 0, count: 0 };
    d.revenue += total;
    d.count += 1;
    dayAcc.set(dayKey, d);

    for (const line of sell.getProducts().getValues()) {
      const name = productDisplayName(line);
      const row = productAcc.get(name) ?? { quantity: 0, revenue: 0 };
      row.quantity += line.quantity;
      row.revenue += line.price * line.quantity;
      productAcc.set(name, row);
    }
  }

  const saleCount = sellsInPeriod.length;
  const avgTicket = saleCount ? totalRevenue / saleCount : 0;

  const byPayment: PaymentSlice[] = Array.from(paymentAcc.entries())
    .map(([method, v]) => ({
      method,
      label: paymentMethodLabel[method],
      count: v.count,
      revenue: v.revenue,
    }))
    .sort((a, b) => b.revenue - a.revenue);

  const daily: DailyPoint[] = [];
  const d = start.clone();
  while (d.isSameOrBefore(end, "day")) {
    const dayKey = d.format("YYYY-MM-DD");
    const v = dayAcc.get(dayKey);
    daily.push({
      dayKey,
      label: d.format("DD/MM"),
      revenue: v?.revenue ?? 0,
      count: v?.count ?? 0,
    });
    d.add(1, "day");
  }

  const dayCount = daily.length;

  const topProducts: TopProductRow[] = Array.from(productAcc.entries())
    .map(([name, v]) => ({
      name,
      quantity: v.quantity,
      revenue: v.revenue,
    }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 8);

  return {
    dayCount,
    periodLabelHuman,
    totalRevenue,
    saleCount,
    avgTicket,
    byPayment,
    daily,
    topProducts,
    sellsInPeriod,
  };
}
